import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Image from "../../../components/common/Image";
import CommentPost from "./CommentPost";
import FeedCommentItem from "./FeedCommentItem";
import List from "./List";

interface CommentThreadProps {
  comment: any;
  postOwnerId: number | string;
  currentUserId: number | string;
  replyGroup?: { rows?: any[]; comment_id?: string | number };
  isReplying: boolean;
  isEditing: boolean;
  isSubmitting: boolean;
  onLike: (item: any) => void;
  onReply: (item: any) => void;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  onReport: (item: any) => void;
  onLoadReplies: (item: any) => void;
  onLoadMoreReplies: (item: any) => void;
  postData: any;
  onEditDone: () => void;
}

const CommentThread = ({
  comment,
  postOwnerId,
  currentUserId,
  replyGroup,
  isReplying,
  isEditing,
  isSubmitting,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onLoadReplies,
  onLoadMoreReplies,
  postData,
  onEditDone,
}: CommentThreadProps) => {
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [keepThreadVisible, setKeepThreadVisible] = useState(false);
  const threadRef = useRef<HTMLLIElement>(null);
  const loadedReplies = replyGroup?.rows || [];
  const hasLoadedReplies = loadedReplies.length > 0;
  const replyCount = Number(comment.reply_count) || 0;

  const showReplies = repliesExpanded && hasLoadedReplies;
  const showReplyThread = showReplies || isReplying;
  const canLoadMoreReplies = showReplies && loadedReplies.length < replyCount;

  const handleViewReplies = () => {
    if (repliesExpanded) {
      setRepliesExpanded(false);
    } else {
      setRepliesExpanded(true);
      setKeepThreadVisible(true);
      if (!hasLoadedReplies) {
        onLoadReplies(comment);
      }
    }
  };

  useEffect(() => {
    if (!keepThreadVisible || !repliesExpanded || !hasLoadedReplies) return;

    threadRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setKeepThreadVisible(false);
  }, [hasLoadedReplies, keepThreadVisible, repliesExpanded]);

  const commentAuthorName =
    `${comment?.user?.first_name || ""} ${comment?.user?.last_name || ""}`.trim() ||
    "User";

  const commentBody =
    isSubmitting ? (
      <div className="comment-loading">
        <CircularProgress color="secondary" size={20} />
      </div>
    ) : comment.comment_type === "image" ? (
      <Image src={comment.comment_text} alt="Comment attachment" />
    ) : (
      <p
        className="comment-text"
        dangerouslySetInnerHTML={{
          __html: comment?.comment_text?.replace(/(\r\n|\n|\r)/gm, "<br/>"),
        }}
      />
    );

  const parentComment = (
    <FeedCommentItem
      item={comment}
      postOwnerId={postOwnerId}
      currentUserId={currentUserId}
      onLike={onLike}
      onReply={onReply}
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={onReport}
      body={commentBody}
      isReplyActive={isReplying}
      isEditing={isEditing}
      editComposer={
        isEditing ? (
          <CommentPost
            data={postData}
            item={comment}
            setComment={onEditDone}
            reason="edit-comment"
          />
        ) : undefined
      }
      replyComposer={undefined}
    />
  );

  return (
    <li
      ref={threadRef}
      className={`comment-thread ${showReplyThread ? "has-replies" : ""}`}
    >
      <div className="comment-thread__parent">
        {parentComment}

        {/* View / hide replies button */}
        {replyCount > 0 && !isReplying && (
          <button
            type="button"
            className="view-replies-btn"
            onClick={handleViewReplies}
            aria-expanded={repliesExpanded}
            aria-controls={`comment-replies-${comment.id}`}
          >
            {repliesExpanded ? (
              <>
                Hide Replies
                <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                View {replyCount} {replyCount === 1 ? "Reply" : "Replies"}
                <KeyboardArrowDownIcon />
              </>
            )}
          </button>
        )}
      </div>

      {/* Inline reply composer */}
      {isReplying && (
        <div className="comment-reply-inline">
          <CommentPost
            data={postData}
            item={comment}
            setComment={onEditDone}
            reason="reply-comment"
          />
        </div>
      )}

      {/* Loaded replies */}
      {showReplies && (
        <div id={`comment-replies-${comment.id}`} className="comment-thread__replies">
          <div className="comment-replies">
            <List
              parentAuthorName={commentAuthorName}
              data={{
                ...replyGroup,
                rows: loadedReplies.map((row: any) => ({
                  ...row,
                  post_user_id: postOwnerId,
                })),
              }}
              postData={postData}
            />
            {canLoadMoreReplies && (
              <button
                type="button"
                className="view-replies-btn view-replies-btn--more"
                onClick={() => onLoadMoreReplies(comment)}
              >
                View more replies
                <KeyboardArrowDownIcon />
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default CommentThread;
